import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PerfectScrollbar from 'react-perfect-scrollbar';

import CustomSelect from '../../components/Forms/CustomSelect/CustomSelect';
import InputItem from '../../components/Forms/InputItem/InputItem';
import Fader from '../../components/Loaders/Fader';
import PollWindow from '../../components/Map/PollWindow/PollWindow';
import PollCard from '../../components/PollCard/PollCard';
import ActivityMapLeaflet from '../../components/ActivityMapLeaflet/ActivityMapLeaflet';

import { getWardData, getWardMessagePoints, getWardMessagesInfo, getWardsShapesByCityId } from '../../../http/wardsAPI';
import {
  getPollData,
  getPollMessagePoints,
  getPollMessagesInfo,
  getPollsShapesByRidingId,
} from '../../../http/pollsAPI';
import {
  FEEDBACK_TYPES,
  SOCIAL_SERVICES_SUBTYPES,
  TIME_PERIOD_TYPES,
  USER_ROLES,
} from '../../../util/constants/AppConstants';

import SearchIcon from '../../../assets/images/icons/search.svg';
import './ActivityMap.scss';

dayjs.extend(utc);

const ActivityMap = () => {
  const config = useSelector((state) => state.config.config);

  const [geometryLoaded, setGeometryLoaded] = useState(false);
  const [polygons, setPolygons] = useState([]);
  const [cp, setCp] = useState([]);
  const [messagePoints, setMessagePoints] = useState([]);
  const [mapData, setMapData] = useState(false);
  const [mapType, setMapType] = useState('Emails');
  const [category, setCategory] = useState('All');
  const [subcategory, setSubcategory] = useState('All');
  const [timePeriod, setTimePeriod] = useState(60);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateError, setDateError] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isPolygonWindowOpen, setIsPolygonWindowOpen] = useState(false);
  const [polygonDemographics, setPolygonDemographics] = useState(null);
  const [polygonMessages, setPolygonMessages] = useState(null);
  const [polygonsLoaded, setPolygonsLoaded] = useState(false);
  const [pointsLoaded, setPointsLoaded] = useState(false);

  const { userInfo } = useOutletContext();

  const councillorRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;
  const isCouncillor = userInfo && userInfo.role === councillorRoleValue;

  useEffect(() => {
    const loadShapes = async () => {
      if (!config || !userInfo) {
        setTimeout(loadShapes, 1000);
        return;
      }

      try {
        const res = isCouncillor
          ? await getWardsShapesByCityId(config, userInfo.city)
          : await getPollsShapesByRidingId(config, userInfo.riding);

        const result = isCouncillor ? res?.data?.rows?.city?.[0] : res?.data?.rows?.riding?.[0];
        const centerPoint = result?.cp ?? [0, 0];

        setPolygons(res.data.rows[isCouncillor ? 'wards' : 'polls']);
        setCp(centerPoint);

        setGeometryLoaded(true);
      } catch (err) {
        console.error('Error loading shapes:', err);
      }
    };

    loadShapes();
  }, [config, userInfo]);

  useEffect(() => {
    setMapData(polygonsLoaded && pointsLoaded);
  }, [polygonsLoaded, pointsLoaded]);

  const checkIsValid = () =>
    mapType === 'Votes' || timePeriod !== 'custom' || (timePeriod === 'custom' && !dateError && startDate && endDate);

  const getFormattedPeriod = () => {
    if (mapType === 'Votes' || timePeriod !== 'custom' || !startDate || !endDate) {
      return { formattedStart: null, formattedEnd: null };
    }

    return {
      formattedStart: dayjs(startDate).startOf('day').utc().format('YYYY-MM-DD HH:mm:ss'),
      formattedEnd: dayjs(endDate).endOf('day').utc().format('YYYY-MM-DD HH:mm:ss'),
    };
  };

  const loadMessagesInfo = async ({ fetchFn, areaId, areaKey, mapExtra = () => ({}), forceAllMessages = false }) => {
    if (!checkIsValid()) return;

    try {
      setPolygonsLoaded(false);

      const { formattedStart, formattedEnd } = getFormattedPeriod();

      const res = await fetchFn(
        config,
        userInfo.id,
        areaId,
        forceAllMessages ? 'All' : category,
        forceAllMessages ? 'All' : subcategory,
        forceAllMessages ? 'all' : timePeriod,
        formattedStart,
        formattedEnd,
      );

      const dataMap = Object.fromEntries(res.data.map((item) => [item[areaKey], item]));

      const updatedPolygons = polygons.map((p) => {
        const item = dataMap[p.id];
        if (!item) {
          return { ...p, message_count: 0, constituents_count: 0 };
        }

        return {
          ...p,
          label: item.label,
          message_count: item.message_count,
          last_message_date: item.last_message_date,
          population: item.population,
          constituents_count: item.constituents_count,
          top_category: item.top_category,
          ...mapExtra(item),
        };
      });

      setPolygons(updatedPolygons);
      setPolygonsLoaded(true);
    } catch (err) {
      console.error('Error loading area emails info:', err);
    }
  };

  const loadMessagePoints = async () => {
    setPointsLoaded(false);

    const { formattedStart, formattedEnd } = getFormattedPeriod();

    try {
      const res = isCouncillor
        ? await getWardMessagePoints(
            config,
            userInfo.id,
            userInfo.city,
            category,
            subcategory,
            timePeriod,
            formattedStart,
            formattedEnd,
          )
        : await getPollMessagePoints(
            config,
            userInfo.id,
            userInfo.riding,
            category,
            subcategory,
            timePeriod,
            formattedStart,
            formattedEnd,
          );

      setMessagePoints(res.data ?? []);
      setPointsLoaded(true);
    } catch (e) {
      console.error('Failed to load email points', e);
    }
  };

  const handleGetPollMessagesInfo = async (forceAll = false) => {
    loadMessagesInfo({
      fetchFn: getPollMessagesInfo,
      areaId: userInfo.riding,
      areaKey: 'poll_id',
      forceAllMessages: forceAll,
      mapExtra: (poll) => ({
        poll_nr: poll.poll_nr,
        win_percentage: poll.win_percentage,
        winner_party_id: poll.winner_party_id,
        winner_party_name: poll.winner_party_name,
        second_party_id: poll.second_party_id,
        second_party_name: poll.second_party_name,
      }),
    });

    await loadMessagePoints();
  };

  const handleGetWardMessagesInfo = async () => {
    loadMessagesInfo({
      fetchFn: getWardMessagesInfo,
      areaId: userInfo.city,
      areaKey: 'ward_id',
      mapExtra: (ward) => ({
        ward_nr: ward.ward_nr,
      }),
    });

    await loadMessagePoints();
  };

  useEffect(() => {
    if (!config || !userInfo || !geometryLoaded) return;

    const loadData = async () => {
      if (isCouncillor) {
        await handleGetWardMessagesInfo();
      } else {
        await handleGetPollMessagesInfo();
      }
    };

    loadData();
  }, [config, userInfo, geometryLoaded]);

  useEffect(() => {
    if (!config || !userInfo || !geometryLoaded) return;

    const loadData = async () => {
      await handleGetPollMessagesInfo(mapType !== 'Emails');
    };

    if (!isCouncillor) {
      loadData();
    }
  }, [mapType]);

  const handlePolygonClick = async (polygonId) => {
    try {
      setIsPolygonWindowOpen(true);

      const { formattedStart, formattedEnd } = getFormattedPeriod();

      const res = isCouncillor
        ? await getWardData(
            config,
            userInfo.id,
            polygonId,
            category,
            subcategory,
            timePeriod,
            formattedStart,
            formattedEnd,
          )
        : await getPollData(
            config,
            userInfo.id,
            polygonId,
            category,
            subcategory,
            timePeriod,
            formattedStart,
            formattedEnd,
          );
      setPolygonDemographics(res.data.demographics ?? []);
      setPolygonMessages(res.data.messages ?? []);
    } catch (err) {
      console.error(`Failed to load polygon data ${polygonId}:`, err);
    }
  };

  const closePolygonWindow = () => {
    setIsPolygonWindowOpen(false);
    setPolygonDemographics(null);
    setPolygonMessages(null);
  };

  return (
    <div className={`activity-map ${isCouncillor ? 'councillor' : ''}`}>
      {!isCouncillor && (
        <div className="activity-map__tabs">
          <div
            className={`activity-map__tab ${mapType === 'Emails' ? 'active' : ''}`}
            onClick={() => setMapType('Emails')}
          >
            Emails
          </div>
          <div
            className={`activity-map__tab ${mapType === 'Votes' ? 'active' : ''}`}
            onClick={() => setMapType('Votes')}
          >
            Votes
          </div>
        </div>
      )}
      {mapType === 'Emails' && (
        <div className="activity-map__filters">
          <div className="activity-map__row">
            <CustomSelect
              label="Category"
              options={[{ label: 'All', value: 'All' }, ...FEEDBACK_TYPES]}
              selectedOption={category}
              setSelectedOption={setCategory}
            />
            {category === 'Social Services' && (
              <CustomSelect
                label="Subcategory"
                options={[{ label: 'All', value: 'All' }, ...SOCIAL_SERVICES_SUBTYPES]}
                selectedOption={subcategory}
                setSelectedOption={setSubcategory}
              />
            )}
            <CustomSelect
              label="Time period"
              options={TIME_PERIOD_TYPES}
              selectedOption={timePeriod}
              setSelectedOption={setTimePeriod}
            />
            {timePeriod !== 'custom' && (
              <div
                className={`activity-map__btn create-btn secondary ${checkIsValid() ? '' : 'disabled'}`}
                onClick={isCouncillor ? handleGetWardMessagesInfo : handleGetPollMessagesInfo}
              >
                Apply
              </div>
            )}
          </div>
          {timePeriod === 'custom' && (
            <div className="activity-map__row">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start date"
                  value={startDate}
                  onChange={(value) => setStartDate(value)}
                  onError={(err) => {
                    setDateError(!!err);
                  }}
                  slotProps={{
                    textField: {
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
                <DatePicker
                  label="End date"
                  value={endDate}
                  onChange={(value) => setEndDate(value)}
                  onError={(err) => {
                    setDateError(!!err);
                  }}
                  slotProps={{
                    textField: {
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </LocalizationProvider>
              <div
                className={`activity-map__btn create-btn secondary ${checkIsValid() ? '' : 'disabled'}`}
                onClick={isCouncillor ? handleGetWardMessagesInfo : handleGetPollMessagesInfo}
              >
                Apply
              </div>
            </div>
          )}
        </div>
      )}
      <div className="activity-map__content">
        <div className="activity-map__sidebar">
          <div className="activity-map__header">
            <div className="activity-map__header-info">
              <h3>
                {mapType === 'Emails' ? `Recent ${isCouncillor ? 'Wards' : 'Polls'}` : `${polygons.length} Polls`}
              </h3>
              {mapData && mapType === 'Emails' && (
                <p>
                  {polygons.filter((p) => p.message_count > 0).length} active {isCouncillor ? 'ward' : 'poll'}
                  {polygons.filter((p) => p.message_count > 0).length === 1 ? '' : 's'}
                </p>
              )}
            </div>
            <InputItem
              type="number"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              label={`Search by ${isCouncillor ? 'ward' : 'poll'} nr`}
              icon={SearchIcon}
            />
          </div>
          <div
            className={`activity-map__polls-wrap ${mapType.toLowerCase()} ${timePeriod === 'custom' ? 'expanded' : ''}`}
          >
            {mapData ? (
              <PerfectScrollbar
                options={{
                  wheelPropagation: false,
                  autoHide: false,
                }}
              >
                <div className="activity-map__polls">
                  {polygons
                    .filter((p) => (mapType === 'Emails' ? p.message_count > 0 : true))
                    .filter((p) => {
                      if (!searchValue) return true;

                      const areaNumberStr = p[`${isCouncillor ? 'ward' : 'poll'}_nr`].toString().padStart(3, '0');
                      const searchStr = searchValue.toString();

                      return areaNumberStr.includes(searchStr);
                    })
                    .sort(
                      (a, b) => a[`${isCouncillor ? 'ward' : 'poll'}_nr`] - b[`${isCouncillor ? 'ward' : 'poll'}_nr`],
                    )
                    .map((p) => (
                      <PollCard poll={p} handlePollClick={handlePolygonClick} />
                    ))}
                </div>
              </PerfectScrollbar>
            ) : (
              <div className="wrap">
                <Fader />
              </div>
            )}
          </div>
        </div>
        {mapData ? (
          <div className={`activity-map__wrap ${mapType.toLowerCase()} ${timePeriod === 'custom' ? 'expanded' : ''}`}>
            <div className="activity-map__box-wrap">
              <div className="activity-map__box">
                <ActivityMapLeaflet
                  polygons={polygons}
                  messagePoints={messagePoints}
                  mapType={mapType}
                  cp={cp}
                  handlePolygonClick={handlePolygonClick}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="wrap">
            <Fader />
          </div>
        )}
      </div>
      {isPolygonWindowOpen && (
        <PollWindow
          messages={polygonMessages}
          data={polygonDemographics}
          isOpen={isPolygonWindowOpen}
          onCloseHandler={closePolygonWindow}
          userInfo={userInfo}
        />
      )}
    </div>
  );
};

export default ActivityMap;
